import { Check, Copy, Loader2 } from "lucide-react";
import QRCode from "qrcode";
import * as React from "react";

import { useTOTPBegin, useTOTPComplete } from "@/api/mfa";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

/**
 * TOTP enrollment dialog — QR + code input (#597 Phase 3).
 *
 * Two-step flow inside a single modal:
 *
 * 1. Server mints a fresh secret (``useTOTPBegin`` → ``otpauth_uri``
 *    + ``secret_code``). The dialog renders a QR image from the URI
 *    (client-side via the ``qrcode`` lib, no network round-trip) and
 *    exposes the secret as manual-entry fallback.
 * 2. User types the 6-digit code from their authenticator app; the
 *    server calls ``VerifySoftwareToken`` + ``SetUserMFAPreference``
 *    in one round-trip (``useTOTPComplete``).
 *
 * The ``begin`` mutation fires ONCE per dialog opening. Cognito emits
 * a fresh secret on every associate call — calling it twice would
 * leave the first QR dead and the second live, silently confusing a
 * user who scanned the first one. The ``ready`` guard here + the
 * disabled-when-pending state prevent that.
 */
export function TotpEnrollDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): JSX.Element {
  const begin = useTOTPBegin();
  const complete = useTOTPComplete();
  const [code, setCode] = React.useState("");
  const [qrDataUrl, setQrDataUrl] = React.useState<string | null>(null);
  const [qrRenderFailed, setQrRenderFailed] = React.useState(false);
  const [secretCopied, setSecretCopied] = React.useState(false);
  // Rupert PR-608 r0 ADVISORY #1: React Strict Mode double-invokes
  // mount effects to catch impurity; a guard keyed on
  // ``begin.isPending || begin.isSuccess`` is racy because those flags
  // update ASYNCHRONOUSLY (the second effect body sees them still
  // false), so we fire ``AssociateSoftwareToken`` twice. Cognito mints
  // a fresh secret each call — the first QR the user scanned goes
  // dead, silently. A ref updates synchronously and survives both
  // Strict-Mode invokes, so this guard is race-free.
  const startedRef = React.useRef(false);

  // Fire the associate call once per open. Reset local UI state on
  // close so re-opening starts clean.
  React.useEffect(() => {
    if (!open) {
      begin.reset();
      complete.reset();
      setCode("");
      setQrDataUrl(null);
      setQrRenderFailed(false);
      setSecretCopied(false);
      startedRef.current = false;
      return;
    }
    if (!startedRef.current) {
      startedRef.current = true;
      begin.mutate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- begin/complete are mutation hooks; running once per `open` is intentional
  }, [open]);

  // Render the QR whenever a fresh otpauth URI lands. Small (~192×192)
  // + ``margin: 1`` to keep the modal from bulging. The library
  // returns a PNG data URL we can drop straight into an <img>.
  React.useEffect(() => {
    if (!begin.data?.otpauth_uri) {
      return;
    }
    let cancelled = false;
    QRCode.toDataURL(begin.data.otpauth_uri, { margin: 1, width: 192 })
      .then((url) => {
        if (!cancelled) {
          setQrDataUrl(url);
          setQrRenderFailed(false);
        }
      })
      .catch((err: unknown) => {
        // Rupert PR-608 r0 ADVISORY #3: rendering failure is soft —
        // the manual-entry secret is still usable — but a user relying
        // on the QR shouldn't stare at empty space. Flip a flag so the
        // fallback copy renders next to the (empty) QR slot.
        console.warn("QR render failed", err);
        if (!cancelled) setQrRenderFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [begin.data?.otpauth_uri]);

  const canSubmit = /^\d{6}$/.test(code) && begin.isSuccess && !complete.isPending;

  const onSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    if (!canSubmit) return;
    complete.mutate(
      { code },
      {
        onSuccess: () => {
          // Close the dialog once the server flipped TOTP on. The
          // ``useTOTPComplete`` mutation already invalidated the
          // factors query, so the Security section re-renders as
          // "enrolled" on close.
          onOpenChange(false);
        },
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Set up authenticator app</DialogTitle>
          <DialogDescription>
            Scan the QR with your authenticator app, then enter the 6-digit code it shows.
          </DialogDescription>
        </DialogHeader>

        {begin.isPending && (
          <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Minting secret…
          </div>
        )}

        {begin.isError && (
          <p className="text-sm text-destructive" role="alert">
            Couldn&apos;t start enrollment. Close this dialog and try again.
          </p>
        )}

        {begin.isSuccess && begin.data && (
          <form onSubmit={onSubmit} className="flex flex-col gap-4">
            <div className="flex flex-col items-center gap-3 rounded-md border border-border-subtle bg-surface-2/50 p-4">
              {qrDataUrl ? (
                <img src={qrDataUrl} alt="TOTP QR code" width={192} height={192} />
              ) : qrRenderFailed ? (
                <div
                  className="flex h-48 w-48 items-center justify-center px-3 text-center text-xs text-muted-foreground"
                  role="alert"
                >
                  QR unavailable — use the code below to enter this manually in your authenticator
                  app.
                </div>
              ) : (
                <div className="flex h-48 w-48 items-center justify-center text-xs text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                </div>
              )}
              <SecretDisplay
                secret={begin.data.secret_code}
                copied={secretCopied}
                setCopied={setSecretCopied}
              />
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="totp-code">Verification code</Label>
              <Input
                id="totp-code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="\d{6}"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="123456"
              />
              {complete.isError && (
                <p className="text-xs text-destructive" role="alert">
                  {formatCompleteError(complete.error)}
                </p>
              )}
            </div>

            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={!canSubmit}>
                {complete.isPending ? (
                  <>
                    <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" aria-hidden />
                    Verifying…
                  </>
                ) : (
                  "Verify and enable"
                )}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

function SecretDisplay({
  secret,
  copied,
  setCopied,
}: {
  secret: string;
  copied: boolean;
  setCopied: (v: boolean) => void;
}): JSX.Element {
  const copy = (): void => {
    // Same navigator.clipboard defensive pattern as Settings.tsx
    // CodeBlock — undefined in insecure contexts / older Safari.
    if (!navigator.clipboard?.writeText) return;
    navigator.clipboard.writeText(secret).then(
      () => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1600);
      },
      () => {
        // Silent — a permission denial doesn't block the flow.
      }
    );
  };
  return (
    <div className="flex w-full flex-col gap-1">
      <span className="label-micro text-muted-foreground">Or enter this key manually</span>
      <div className="flex items-center gap-2">
        <code className="min-w-0 flex-1 truncate rounded border border-border-subtle bg-background px-2 py-1 font-mono text-xs text-foreground">
          {secret}
        </code>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label={copied ? "Copied" : "Copy secret"}
          onClick={copy}
        >
          {copied ? (
            <Check className="h-3.5 w-3.5 text-state-cached-fg" aria-hidden />
          ) : (
            <Copy className="h-3.5 w-3.5" aria-hidden />
          )}
        </Button>
      </div>
    </div>
  );
}

function formatCompleteError(err: unknown): string {
  if (err && typeof err === "object" && "message" in err) {
    const msg = String((err as { message: unknown }).message);
    // Standard ApiErrorBody messages from the backend are already
    // human — pass through. The two most-common ones we shape:
    if (msg.toLowerCase().includes("mismatch")) {
      return "That code didn't match. Try the next one your app shows.";
    }
    if (msg.toLowerCase().includes("expired")) {
      return "That code expired. Try the next one your app shows.";
    }
    return msg;
  }
  return "Verification failed. Try again.";
}
